import React, { useState, useRef, useEffect, useCallback } from 'react';
import { costService } from '../services/costService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileInvoice,
  faDownload,
  faEye,
  faCheck,
  faClock,
  faExclamationCircle,
  faPlus,
  faSearch,
  faCalendar,
  faDollarSign,
  faTimes,
  faSpinner,
  faFilePdf,
  faChevronLeft,
  faChevronRight,
  faCalendarAlt,
  faPaperclip,
  faTrash,
  faUpload,
  faRobot,
  faCheckCircle,
  faLink,
  faFolderOpen,
  faCircleNotch,
  faInfoCircle,
  faTerminal,
  faRefresh,
} from '@fortawesome/free-solid-svg-icons';
import { faAws, faGoogle, faGithub } from '@fortawesome/free-brands-svg-icons';
import { useAIAnalysis, AIInsightsPanel, AIInsightsButton } from './AIInsightsPanel';
import { useGoogleLogin } from '@react-oauth/google';

interface BillsTabProps {
  data: any;
  loading?: boolean;
}

interface Bill {
  id: string;
  month: string;              // Display: "April 2026"
  monthKey: string;           // Filter:  "2026-04"
  provider: 'aws' | 'gcp' | 'github' | 'custom';
  accountName: string;
  amount: number;
  status: 'paid' | 'pending' | 'overdue';
  dueDate?: string;
  paidDate?: string;
  invoiceNumber?: string;
  currency: string;
  fileName?: string;
  fileType?: string;
  fileSize?: number;
  s3Key?: string;
}

const PROVIDER_ICON: Record<string, any> = { aws: faAws, gcp: faGoogle, github: faGithub };
const PROVIDER_COLOR: Record<string, string> = {
  aws: 'text-orange-600',
  gcp: 'text-blue-600',
  github: 'text-gray-600',
  custom: 'text-purple-600'
};

const getProviderIcon = (p: string) => PROVIDER_ICON[p] || faFileInvoice;
const getProviderColor = (p: string) => PROVIDER_COLOR[p] || 'text-purple-600';

const getStatusBadge = (status: string) => {
  switch (status) {
    case 'paid':
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-[10px] font-medium bg-green-100 text-green-700">
          <FontAwesomeIcon icon={faCheck} className="mr-1" />
          Paid
        </span>
      );
    case 'pending':
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-[10px] font-medium bg-yellow-100 text-yellow-700">
          <FontAwesomeIcon icon={faClock} className="mr-1" />
          Pending
        </span>
      );
    case 'overdue':
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-[10px] font-medium bg-red-100 text-red-700">
          <FontAwesomeIcon icon={faExclamationCircle} className="mr-1" />
          Overdue
        </span>
      );
    default:
      return null;
  }
};

const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const formatMonthDisplay = (monthKey: string) => {
  const [year, month] = monthKey.split('-');
  const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

interface BillFormState {
  monthKey: string;
  provider: 'aws' | 'gcp' | 'github' | 'custom';
  accountName: string;
  invoiceNumber: string;
  amount: string;
  currency: string;
  dueDate: string;
  paidDate: string;
  status: 'paid' | 'pending' | 'overdue';
  file: File | null;
}

const blankForm = (monthKey: string): BillFormState => ({
  monthKey,
  provider: 'custom',
  accountName: '',
  invoiceNumber: '',
  amount: '',
  currency: 'USD',
  dueDate: '',
  paidDate: '',
  status: 'pending',
  file: null
});

interface ParsedItem {
  vendor: string;
  invoiceNumber: string;
  amount: number;
  currency: string;
  month: string;
  dueDate: string;
  provider: string;
  accountName: string;
  notes: string;
  _filename: string;
  _fileSize: number;
  _file: File | undefined;
  _parseSuccess: boolean;
  _saved: boolean;
  _saving: boolean;
  _saveError: string;
}

const BulkUploadModal: React.FC<{
  onClose: () => void;
  onSaved: (bills: Bill[]) => void;
}> = ({ onClose, onSaved }) => {
  const [files, setFiles] = useState<File[]>([]);
  const [stage, setStage] = useState<'select' | 'parsing' | 'review'>('select');
  const [items, setItems] = useState<ParsedItem[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [savedBills, setSavedBills] = useState<Bill[]>([]);
  const [savingAll, setSavingAll] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const bulkFileRef = useRef<HTMLInputElement>(null);

  const addFiles = (incoming: File[]) => {
    const allowed = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    const valid = incoming.filter(f => allowed.includes(f.type));
    setFiles(prev => {
      const existing = new Set(prev.map(f => f.name + f.size));
      return [...prev, ...valid.filter(f => !existing.has(f.name + f.size))];
    });
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(Array.from(e.dataTransfer.files));
  };

  const handleFilePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(e.target.files || []));
    if (e.target) e.target.value = '';
  };

  const removeFile = (idx: number) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const handleParse = async () => {
    if (files.length === 0) return;
    setStage('parsing');
    setParseError(null);
    try {
      const results = await costService.bulkParseBills(files);
      const parsed: ParsedItem[] = results.map((r: any, i: number) => ({
        vendor: r.data?.vendor || '',
        invoiceNumber: r.data?.invoiceNumber || '',
        amount: r.data?.amount ?? 0,
        currency: r.data?.currency || 'USD',
        month: r.data?.month || getCurrentMonth(),
        dueDate: r.data?.dueDate || '',
        provider: r.data?.provider || 'custom',
        accountName: r.data?.accountName || '',
        notes: r.data?.notes || '',
        _filename: r.data?._filename || files[i]?.name || '',
        _fileSize: r.data?._fileSize || files[i]?.size || 0,
        _file: files[i],
        _parseSuccess: r.success !== false,
        _saved: false,
        _saving: false,
        _saveError: r.success === false ? (r.error || 'Parse failed') : '',
      }));
      setItems(parsed);
      setStage('review');
    } catch (err: any) {
      setParseError(err?.message || 'Parsing failed');
      setStage('select');
    }
  };

  const updateItem = (idx: number, patch: Partial<ParsedItem>) =>
    setItems(prev => prev.map((item, i) => i === idx ? { ...item, ...patch } : item));

  const saveItem = async (idx: number): Promise<Bill | null> => {
    const item = items[idx];
    if (item._saved) return null;
    updateItem(idx, { _saving: true, _saveError: '' });
    try {
      const saved = await costService.createBill({
        month: item.month,
        provider: item.provider as any,
        accountName: item.accountName || item.vendor,
        amount: item.amount,
        currency: item.currency,
        status: 'pending',
        dueDate: item.dueDate || undefined,
        invoiceNumber: item.invoiceNumber || undefined,
        file: item._file || null,
      });
      updateItem(idx, { _saving: false, _saved: true });
      return saved as Bill;
    } catch (err: any) {
      updateItem(idx, { _saving: false, _saveError: err?.message || 'Save failed' });
      return null;
    }
  };

  const handleSaveAll = async () => {
    setSavingAll(true);
    const saved: Bill[] = [];
    for (let i = 0; i < items.length; i++) {
      if (!items[i]._saved) {
        const bill = await saveItem(i);
        if (bill) saved.push(bill);
      }
    }
    setSavedBills(prev => [...prev, ...saved]);
    setSavingAll(false);
  };

  const handleDone = () => {
    if (savedBills.length > 0) onSaved(savedBills);
    onClose();
  };

  const unsavedCount = items.filter(i => !i._saved).length;
  const allSaved = items.length > 0 && unsavedCount === 0;

  return (
    <div className="fixed inset-0 lg:left-[30%] bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 flex items-center">
              <FontAwesomeIcon icon={faRobot} className="text-indigo-600 mr-2" />
              Bulk Invoice Upload
            </h3>
            <p className="text-[10px] text-gray-500 mt-0.5">Upload multiple PDFs — AI extracts invoice data automatically</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Stage: select */}
          {stage === 'select' && (
            <div className="space-y-3">
              {parseError && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded p-3">
                  {parseError}
                </div>
              )}

              {/* Drop zone */}
              <div
                onDrop={handleDrop}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onClick={() => bulkFileRef.current?.click()}
                className={`border-2 border-dashed rounded p-8 text-center cursor-pointer transition-colors ${
                  dragOver ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 hover:border-indigo-400 hover:bg-gray-50'
                }`}
              >
                <FontAwesomeIcon icon={faUpload} className="text-3xl text-gray-400 mb-3" />
                <div className="text-sm font-medium text-gray-700 mb-1">Drop invoice PDFs here</div>
                <div className="text-xs text-gray-500">or click to browse — PDF, PNG, JPG (max 10MB each, up to 10 files)</div>
                <input
                  ref={bulkFileRef}
                  type="file"
                  multiple
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={handleFilePick}
                  className="hidden"
                />
              </div>

              {/* File list */}
              {files.length > 0 && (
                <div className="space-y-2">
                  {files.map((f, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded px-3 py-2">
                      <div className="flex items-center min-w-0">
                        <FontAwesomeIcon icon={faFilePdf} className="text-indigo-500 mr-2 flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="text-xs font-medium text-gray-900 truncate">{f.name}</div>
                          <div className="text-[10px] text-gray-500">{(f.size / 1024).toFixed(1)} KB</div>
                        </div>
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); removeFile(idx); }} className="text-red-500 hover:text-red-700 ml-3 flex-shrink-0">
                        <FontAwesomeIcon icon={faTimes} className="text-xs" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Stage: parsing */}
          {stage === 'parsing' && (
            <div className="flex flex-col items-center justify-center py-16">
              <FontAwesomeIcon icon={faSpinner} className="animate-spin text-3xl text-indigo-600 mb-4" />
              <div className="text-sm font-medium text-gray-900 mb-1">Parsing {files.length} invoice{files.length > 1 ? 's' : ''} with AI...</div>
              <div className="text-xs text-gray-500">Claude Haiku is extracting vendor, amounts, and dates</div>
            </div>
          )}

          {/* Stage: review */}
          {stage === 'review' && (
            <div className="space-y-3">
              {items.map((item, idx) => (
                <div key={idx} className={`border rounded p-3 ${item._saved ? 'border-green-300 bg-green-50' : item._saveError ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'}`}>
                  {/* Card header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center min-w-0">
                      <FontAwesomeIcon icon={faFilePdf} className="text-indigo-500 mr-2 flex-shrink-0" />
                      <span className="text-[10px] text-gray-500 truncate">{item._filename}</span>
                      {!item._parseSuccess && (
                        <span className="ml-2 px-1.5 py-0.5 bg-yellow-100 text-yellow-700 text-[10px] rounded">Parse failed — please fill manually</span>
                      )}
                    </div>
                    {item._saved && (
                      <span className="flex items-center text-[10px] text-green-700 font-medium flex-shrink-0 ml-2">
                        <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
                        Saved
                      </span>
                    )}
                  </div>

                  {/* Editable fields */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                    <div>
                      <label className="block text-gray-500 mb-0.5">Vendor / Account *</label>
                      <input
                        type="text"
                        value={item.accountName || item.vendor}
                        onChange={e => updateItem(idx, { accountName: e.target.value, vendor: e.target.value })}
                        disabled={item._saved}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-500 mb-0.5">Provider</label>
                      <select
                        value={item.provider}
                        onChange={e => updateItem(idx, { provider: e.target.value })}
                        disabled={item._saved}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100"
                      >
                        <option value="aws">AWS</option>
                        <option value="gcp">GCP</option>
                        <option value="github">GitHub</option>
                        <option value="custom">Custom</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-gray-500 mb-0.5">Month *</label>
                      <input
                        type="month"
                        value={item.month}
                        onChange={e => updateItem(idx, { month: e.target.value })}
                        disabled={item._saved}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-500 mb-0.5">Amount *</label>
                      <div className="flex space-x-1">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.amount}
                          onChange={e => updateItem(idx, { amount: Number(e.target.value) })}
                          disabled={item._saved}
                          className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100"
                        />
                        <select
                          value={item.currency}
                          onChange={e => updateItem(idx, { currency: e.target.value })}
                          disabled={item._saved}
                          className="px-1 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100"
                        >
                          <option>USD</option>
                          <option>INR</option>
                          <option>EUR</option>
                          <option>GBP</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-gray-500 mb-0.5">Invoice #</label>
                      <input
                        type="text"
                        value={item.invoiceNumber}
                        onChange={e => updateItem(idx, { invoiceNumber: e.target.value })}
                        disabled={item._saved}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-500 mb-0.5">Due Date</label>
                      <input
                        type="date"
                        value={item.dueDate}
                        onChange={e => updateItem(idx, { dueDate: e.target.value })}
                        disabled={item._saved}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100"
                      />
                    </div>
                    {item.notes && (
                      <div className="col-span-2 sm:col-span-3">
                        <label className="block text-gray-500 mb-0.5">Notes (from AI)</label>
                        <div className="px-2 py-1 bg-gray-50 border border-gray-200 rounded text-[10px] text-gray-600">{item.notes}</div>
                      </div>
                    )}
                  </div>

                  {item._saveError && (
                    <div className="mt-2 text-[10px] text-red-600">{item._saveError}</div>
                  )}

                  {!item._saved && (
                    <div className="mt-2 flex justify-end">
                      <button
                        onClick={() => saveItem(idx).then(b => { if (b) setSavedBills(prev => [...prev, b]); })}
                        disabled={item._saving}
                        className="px-3 py-1 text-xs font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700 disabled:opacity-50"
                      >
                        {item._saving ? <><FontAwesomeIcon icon={faSpinner} className="animate-spin mr-1" />Saving...</> : 'Save'}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200">
          <div className="text-xs text-gray-500">
            {stage === 'select' && `${files.length} file${files.length !== 1 ? 's' : ''} selected`}
            {stage === 'review' && `${items.length - unsavedCount} / ${items.length} saved`}
          </div>
          <div className="flex items-center space-x-3">
            {stage === 'select' && (
              <>
                <button onClick={onClose} className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200">Cancel</button>
                <button
                  onClick={handleParse}
                  disabled={files.length === 0}
                  className="px-4 py-2 text-xs font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700 disabled:opacity-40"
                >
                  <FontAwesomeIcon icon={faRobot} className="mr-2" />
                  Parse with AI
                </button>
              </>
            )}
            {stage === 'review' && (
              <>
                {!allSaved && (
                  <button
                    onClick={handleSaveAll}
                    disabled={savingAll || allSaved}
                    className="px-4 py-2 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50"
                  >
                    {savingAll ? <><FontAwesomeIcon icon={faSpinner} className="animate-spin mr-1" />Saving...</> : `Save All (${unsavedCount})`}
                  </button>
                )}
                <button onClick={handleDone} className="px-4 py-2 text-xs font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700">
                  {allSaved ? 'Done' : 'Skip & Close'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// BOT AUTOMATION MODAL
// Playwright bot backend pe chalta hai — yahan hum sirf UI + SSE stream handle
// karte hain. User creds deta hai → bot login karta hai → invoices download
// karta hai → hum live logs stream se receive karte hain.
// ─────────────────────────────────────────────────────────────────────────────

// Har tool ka naam + default portal URL
const TOOL_OPTIONS = [
  { value: 'mongodb',  label: 'MongoDB Atlas',   defaultUrl: 'https://account.mongodb.com/account/login' },
  { value: 'aws',      label: 'AWS Console',      defaultUrl: 'https://console.aws.amazon.com/billing/home' },
  { value: 'gcp',      label: 'Google Cloud',     defaultUrl: 'https://console.cloud.google.com/billing' },
  { value: 'datadog',  label: 'Datadog',          defaultUrl: 'https://app.datadoghq.com/account/billing' },
  { value: 'github',   label: 'GitHub',           defaultUrl: 'https://github.com/settings/billing' },
  { value: 'azure',    label: 'Microsoft Azure',  defaultUrl: 'https://portal.azure.com/#blade/Microsoft_Azure_Billing' },
];

// Ek log line ka type — terminal mein alag colour se dikhate hain
interface BotLogLine {
  type: 'info' | 'success' | 'error' | 'warn';
  msg: string;
  ts: string; // HH:MM:SS
}

// Backend se jo invoice object aata hai SSE stream mein
interface BotInvoice {
  filename: string;
  amount?: number;
  currency?: string;
  period?: string;    // e.g. "May 2026"
  downloadUrl: string; // S3 signed URL
}

const API_BASE_BOT = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

const BillBotModal: React.FC<{
  onClose: () => void;
  onSaved: (bills: Bill[]) => void;
}> = ({ onClose, onSaved }) => {

  // ── Form state ──────────────────────────────────────────────────────────────
  const [tool, setTool]           = useState('mongodb');
  const [portalUrl, setPortalUrl] = useState(TOOL_OPTIONS[0].defaultUrl);
  const [email, setEmail]         = useState('');
  const [password, setPassword]   = useState('');
  const [instruction, setInstruction] = useState('Download last 3 months invoices');

  // ── Automation state ────────────────────────────────────────────────────────
  // stage machine: form → running → done | error
  // 'form'    = user fields fill kar raha hai
  // 'running' = bot chal raha hai, logs aa rahe hain
  // 'done'    = bot complete, invoices haath mein
  // 'error'   = kuch gadbad hui
  const [stage, setStage]       = useState<'form' | 'running' | 'done' | 'error'>('form');
  const [logs, setLogs]         = useState<BotLogLine[]>([]);
  const [invoices, setInvoices] = useState<BotInvoice[]>([]);
  const [errorMsg, setErrorMsg] = useState('');

  // logs ka last element always visible rahe (auto-scroll)
  const logsEndRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  // Tool dropdown change → default URL update karo
  const handleToolChange = (val: string) => {
    setTool(val);
    const found = TOOL_OPTIONS.find(t => t.value === val);
    if (found) setPortalUrl(found.defaultUrl);
  };

  // Helper: ek log line push karo state mein
  const addLog = (type: BotLogLine['type'], msg: string) =>
    setLogs(prev => [...prev, { type, msg, ts: new Date().toLocaleTimeString() }]);

  // ── MAIN: Bot run karo + SSE stream read karo ───────────────────────────────
  //
  // WHY fetch instead of EventSource?
  // EventSource sirf GET request support karta hai.
  // Hum credentials POST body mein bhej rahe hain (safe) — isliye
  // fetch() use karte hain aur response.body ko ReadableStream ki tarah
  // read karte hain. Same SSE format, bas POST se.
  //
  const handleRun = async () => {
    if (!email.trim() || !password.trim()) return;
    setStage('running');
    setLogs([]);
    setInvoices([]);
    setErrorMsg('');

    try {
      // Step 1: POST request bhejo — backend Playwright bot start karega
      const response = await fetch(`${API_BASE_BOT}/api/bills/automate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tool,
          url: portalUrl,
          email: email.trim(),
          password,
          instruction: instruction.trim(),
        }),
      });

      if (!response.ok || !response.body) {
        throw new Error(`Server error: ${response.status}`);
      }

      // Step 2: SSE stream ko line-by-line padho
      // Backend "data: {...}\n\n" format mein events bhejta hai
      //
      // ReadableStream kya hai?
      // → response.body ek stream hai, matlab data chunks mein aata hai
      // → reader.read() ek chunk deta hai har baar (blocking nahi, async)
      // → decoder.decode() bytes → text convert karta hai
      // → buffer mein rakhte hain incomplete lines ke liye
      //
      const reader  = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer    = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break; // stream khatam

        buffer += decoder.decode(value, { stream: true });

        // '\n' pe split karo — ek SSE event mein multiple lines ho sakti hain
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // last incomplete line buffer mein wapas

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue; // sirf "data:" lines
          try {
            const event = JSON.parse(line.slice(6)); // "data: " hata ke parse karo

            // Backend 4 types ke events bhejta hai:
            // { type: 'log',     level: 'info'|'success'|'warn'|'error', msg }
            // { type: 'invoice', invoice: BotInvoice }
            // { type: 'done',    count: number }
            // { type: 'error',   msg: string }

            if (event.type === 'log') {
              addLog(event.level || 'info', event.msg);

            } else if (event.type === 'invoice') {
              setInvoices(prev => [...prev, event.invoice]);
              addLog('success', `Downloaded: ${event.invoice.filename}`);

            } else if (event.type === 'done') {
              setStage('done');
              addLog('success', `✅ Done! ${event.count} invoice(s) ready`);

            } else if (event.type === 'error') {
              setStage('error');
              setErrorMsg(event.msg);
              addLog('error', event.msg);
            }
          } catch {
            // malformed JSON — ignore karo, agle line pe jao
          }
        }
      }

      // stream end ho gaya lekin 'done' event nahi aaya
      if (stage === 'running') setStage('done');

    } catch (err: any) {
      setStage('error');
      const msg = err.message || 'Automation failed';
      setErrorMsg(msg);
      addLog('error', msg);
    }
  };

  // Log line ka colour — terminal vibes
  const logColor = (type: BotLogLine['type']) => {
    switch (type) {
      case 'success': return 'text-green-400';
      case 'error':   return 'text-red-400';
      case 'warn':    return 'text-yellow-400';
      default:        return 'text-gray-300';
    }
  };

  const logPrefix = (type: BotLogLine['type']) => {
    switch (type) {
      case 'success': return '✓';
      case 'error':   return '✗';
      case 'warn':    return '⚠';
      default:        return '›';
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 lg:left-[30%] bg-black bg-opacity-60 flex items-center justify-center z-50">
      <div className="bg-white rounded shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <FontAwesomeIcon icon={faRobot} className="text-blue-600" />
              Auto Fetch Invoices
              {stage === 'running' && (
                <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin text-[8px]" />
                  Bot running
                </span>
              )}
              {stage === 'done' && (
                <span className="text-[10px] font-medium bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                  Complete
                </span>
              )}
            </h3>
            <p className="text-[10px] text-gray-500 mt-0.5">
              Bot headless browser mein login karta hai aur invoices automatically download karta hai
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">

          {/* ── FORM ───────────────────────────────────────────────────────── */}
          {stage === 'form' && (
            <div className="space-y-3">

              {/* Tool select */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Select Tool
                </label>
                <select
                  value={tool}
                  onChange={e => handleToolChange(e.target.value)}
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                >
                  {TOOL_OPTIONS.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              {/* Portal URL — tool change pe auto-fill, user override kar sakta hai */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Portal URL
                  <span className="text-gray-400 font-normal ml-1">(auto-filled, override kar sakte ho)</span>
                </label>
                <input
                  type="url"
                  value={portalUrl}
                  onChange={e => setPortalUrl(e.target.value)}
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 font-mono"
                />
              </div>

              {/* Credentials */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Email / User ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full px-3 py-2 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Password <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-3 py-2 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Natural language instruction → LangChain parse karega */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Instruction
                  <span className="text-gray-400 font-normal ml-1">(AI samjhega)</span>
                </label>
                <textarea
                  value={instruction}
                  onChange={e => setInstruction(e.target.value)}
                  rows={2}
                  placeholder="e.g. Download last 3 months invoices"
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 resize-none"
                />
                <p className="text-[10px] text-gray-400 mt-0.5">
                  LangChain yeh instruction parse karke bot ko steps deta hai
                </p>
              </div>

              {/* Security note */}
              <div className="bg-amber-50 border border-amber-200 rounded p-2.5 text-[10px] text-amber-700 flex gap-2">
                <FontAwesomeIcon icon={faInfoCircle} className="mt-0.5 shrink-0" />
                <span>
                  Credentials sirf is automation session ke liye use hote hain. Server pe store nahi hote.
                  Production mein HTTPS ensure karo.
                </span>
              </div>
            </div>
          )}

          {/* ── RUNNING / DONE / ERROR — Live terminal log ─────────────────── */}
          {stage !== 'form' && (
            <div className="space-y-3">

              {/* Terminal box — dark background, monospace, logs stream */}
              <div className="bg-gray-950 rounded-lg border border-gray-700 overflow-hidden">
                {/* Terminal title bar */}
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-800 border-b border-gray-700">
                  <div className="flex gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
                    <span className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
                    <span className="w-2.5 h-2.5 rounded-full bg-green-500" />
                  </div>
                  <FontAwesomeIcon icon={faTerminal} className="text-gray-400 text-[10px]" />
                  <span className="text-[10px] text-gray-400 font-mono">
                    devopsx-bot — {TOOL_OPTIONS.find(t => t.value === tool)?.label}
                  </span>
                </div>

                {/* Log lines */}
                <div className="p-3 font-mono text-[11px] h-52 overflow-y-auto space-y-0.5">
                  {logs.length === 0 && (
                    <span className="text-gray-600">Initializing bot...</span>
                  )}
                  {logs.map((log, i) => (
                    <div key={i} className="flex gap-2 leading-relaxed">
                      <span className="text-gray-600 shrink-0 select-none">{log.ts}</span>
                      <span className={`shrink-0 select-none ${logColor(log.type)}`}>
                        {logPrefix(log.type)}
                      </span>
                      <span className={logColor(log.type)}>{log.msg}</span>
                    </div>
                  ))}
                  {stage === 'running' && (
                    <div className="flex items-center gap-1.5 text-blue-400 mt-1">
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin text-[9px]" />
                      <span className="animate-pulse">Working...</span>
                    </div>
                  )}
                  <div ref={logsEndRef} />
                </div>
              </div>

              {/* Downloaded invoices list */}
              {invoices.length > 0 && (
                <div className="space-y-2">
                  <div className="text-xs font-semibold text-gray-700 flex items-center gap-1">
                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-500" />
                    Downloaded Invoices ({invoices.length})
                  </div>
                  {invoices.map((inv, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between bg-green-50 border border-green-200 rounded px-3 py-2"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <FontAwesomeIcon icon={faFilePdf} className="text-green-600 text-xs shrink-0" />
                        <div className="min-w-0">
                          <div className="text-xs font-medium text-gray-900 truncate">
                            {inv.filename}
                          </div>
                          {inv.period && (
                            <div className="text-[10px] text-gray-500">{inv.period}</div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0 ml-2">
                        {inv.amount != null && (
                          <span className="text-xs font-bold text-gray-900">
                            ${inv.amount.toLocaleString()} {inv.currency || 'USD'}
                          </span>
                        )}
                        <a
                          href={inv.downloadUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-green-600 hover:text-green-800 text-xs"
                          title="Download"
                        >
                          <FontAwesomeIcon icon={faDownload} />
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Error state */}
              {stage === 'error' && errorMsg && (
                <div className="bg-red-50 border border-red-200 rounded p-3 text-xs text-red-700">
                  <FontAwesomeIcon icon={faExclamationCircle} className="mr-2" />
                  {errorMsg}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer buttons */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
          >
            {stage === 'done' ? 'Close' : 'Cancel'}
          </button>

          <div className="flex gap-2">
            {/* Form pe: Run Bot */}
            {stage === 'form' && (
              <button
                onClick={handleRun}
                disabled={!email.trim() || !password.trim()}
                className="px-4 py-2 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-40 flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faRobot} />
                Run Bot
              </button>
            )}

            {/* Error pe: dobara try karo */}
            {stage === 'error' && (
              <button
                onClick={() => setStage('form')}
                className="px-4 py-2 text-xs font-medium text-white bg-orange-600 rounded hover:bg-orange-700 flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faRefresh} />
                Try Again
              </button>
            )}

            {/* Done pe: invoices bills mein save karo */}
            {stage === 'done' && invoices.length > 0 && (
              <button
                onClick={() => {
                  // TODO: backend se invoice data Bills collection mein save ho chuka hoga
                  // Yahan parent ko refresh karne ka signal denge
                  onSaved([]);
                  onClose();
                }}
                className="px-4 py-2 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700 flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faCheckCircle} />
                Done ({invoices.length} invoices)
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const DriveParseModal: React.FC<{
  onClose: () => void;
  onSaved: (bills: Bill[]) => void;
}> = ({ onClose, onSaved }) => {
  const [folderUrl, setFolderUrl] = useState('');
  const [stage, setStage] = useState<'input' | 'fetching' | 'review'>('input');
  const [statusMsg, setStatusMsg] = useState('Connecting to Google Drive...');
  const [items, setItems] = useState<ParsedItem[]>([]);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [savedBills, setSavedBills] = useState<Bill[]>([]);
  const [savingAll, setSavingAll] = useState(false);
  const [isPrivateError, setIsPrivateError] = useState(false);

  const googleLogin = useGoogleLogin({
    scope: 'https://www.googleapis.com/auth/drive.readonly',
    onSuccess: (tokenResponse: any) => {
      handleFetch(tokenResponse.access_token);
    },
    onError: () => {
      setFetchError('Google sign-in failed. Please try again.');
    },
  });

  const isValidDriveUrl = (url: string) =>
    /drive\.google\.com\/drive\/(u\/\d+\/)?folders\//.test(url);

  const handleFetch = async (accessToken?: string) => {
    if (!folderUrl.trim()) return;
    setStage('fetching');
    setFetchError(null);
    setIsPrivateError(false);

    const msgs = [
      'Connecting to Google Drive...',
      'Listing PDF files in folder...',
      'Downloading invoices...',
      'Parsing with AI (GPT-4o-mini)...',
    ];
    let mi = 0;
    setStatusMsg(msgs[0]);
    const msgInterval = setInterval(() => {
      mi = Math.min(mi + 1, msgs.length - 1);
      setStatusMsg(msgs[mi]);
    }, 2500);

    try {
      const results = await costService.parseBillsFromDrive(folderUrl.trim(), accessToken);
      clearInterval(msgInterval);
      const parsed: ParsedItem[] = results.map((r: any, i: number) => ({
        vendor: r.data?.vendor || '',
        invoiceNumber: r.data?.invoiceNumber || '',
        amount: r.data?.amount ?? 0,
        currency: r.data?.currency || 'USD',
        month: r.data?.month || getCurrentMonth(),
        dueDate: r.data?.dueDate || '',
        provider: r.data?.provider || 'custom',
        accountName: r.data?.accountName || '',
        notes: r.data?.notes || '',
        _filename: r.data?._filename || `file-${i + 1}`,
        _fileSize: r.data?._fileSize || 0,
        _file: undefined,
        _parseSuccess: r.success !== false,
        _saved: false,
        _saving: false,
        _saveError: r.success === false ? (r.error || 'Parse failed') : '',
      }));
      setItems(parsed);
      setStage('review');
    } catch (err: any) {
      clearInterval(msgInterval);
      const msg: string = err?.message || 'Failed to fetch from Drive';
      setFetchError(msg);
      if (msg.includes('403') || msg.includes('private') || msg.includes('Anyone with') || msg.includes('download')) {
        setIsPrivateError(true);
      }
      setStage('input');
    }
  };

  const updateItem = (idx: number, patch: Partial<ParsedItem>) =>
    setItems(prev => prev.map((item, i) => i === idx ? { ...item, ...patch } : item));

  const saveItem = async (idx: number): Promise<Bill | null> => {
    const item = items[idx];
    if (item._saved) return null;
    updateItem(idx, { _saving: true, _saveError: '' });
    try {
      const saved = await costService.createBill({
        month: item.month,
        provider: item.provider as any,
        accountName: item.accountName || item.vendor,
        amount: item.amount,
        currency: item.currency,
        status: 'pending',
        dueDate: item.dueDate || undefined,
        invoiceNumber: item.invoiceNumber || undefined,
        file: null,
      });
      updateItem(idx, { _saving: false, _saved: true });
      return saved as Bill;
    } catch (err: any) {
      updateItem(idx, { _saving: false, _saveError: err?.message || 'Save failed' });
      return null;
    }
  };

  const handleSaveAll = async () => {
    setSavingAll(true);
    const saved: Bill[] = [];
    for (let i = 0; i < items.length; i++) {
      if (!items[i]._saved) {
        const bill = await saveItem(i);
        if (bill) saved.push(bill);
      }
    }
    setSavedBills(prev => [...prev, ...saved]);
    setSavingAll(false);
  };

  const handleDone = () => {
    if (savedBills.length > 0) onSaved(savedBills);
    onClose();
  };

  const unsavedCount = items.filter(i => !i._saved).length;
  const allSaved = items.length > 0 && unsavedCount === 0;

  return (
    <div className="fixed inset-0 lg:left-[30%] bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 flex items-center">
              <FontAwesomeIcon icon={faFolderOpen} className="text-green-600 mr-2" />
              Import from Google Drive
            </h3>
            <p className="text-[10px] text-gray-500 mt-0.5">Paste a shared Drive folder link — AI parses all PDFs automatically</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">

          {/* Stage: input */}
          {stage === 'input' && (
            <div className="space-y-4">
              {fetchError && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded p-3">
                  {fetchError}
                  {isPrivateError && (
                    <div className="mt-1 text-[10px]">
                      Folder is private. Click <strong>"Sign in with Google & Retry"</strong> below to authorize access, or share it as "Anyone with the link" and retry.
                    </div>
                  )}
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-700">
                <strong>How to get the link:</strong> Google Drive folder → Right-click → Share → "Anyone with the link" → Copy link
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Google Drive Folder URL
                </label>
                <div className="flex space-x-2">
                  <div className="relative flex-1">
                    <FontAwesomeIcon icon={faLink} className="absolute left-3 top-2.5 text-gray-400 text-xs" />
                    <input
                      type="url"
                      placeholder="https://drive.google.com/drive/folders/..."
                      value={folderUrl}
                      onChange={e => setFolderUrl(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && isValidDriveUrl(folderUrl) && handleFetch()}
                      className="w-full pl-9 pr-3 py-2 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                </div>
                {folderUrl && !isValidDriveUrl(folderUrl) && (
                  <p className="text-[10px] text-red-500 mt-1">Not a valid Drive folder URL</p>
                )}
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded p-3 text-[10px] text-gray-500 space-y-1">
                <div>✅ Works with public folders ("Anyone with the link")</div>
                <div>🔐 Private folders — click "Sign in with Google" when prompted</div>
                <div>📄 Supports PDF, PNG, JPG — up to 50 files per folder</div>
              </div>
            </div>
          )}

          {/* Stage: fetching */}
          {stage === 'fetching' && (
            <div className="flex flex-col items-center justify-center py-16 space-y-4">
              <FontAwesomeIcon icon={faCircleNotch} className="animate-spin text-3xl text-green-600" />
              <div className="text-sm font-medium text-gray-900">{statusMsg}</div>
              <div className="text-xs text-gray-400 max-w-xs text-center truncate">{folderUrl}</div>
            </div>
          )}

          {/* Stage: review */}
          {stage === 'review' && (
            <div className="space-y-3">
              <div className="text-xs text-gray-500 bg-green-50 border border-green-200 rounded px-3 py-2">
                Found <strong>{items.length}</strong> invoice{items.length !== 1 ? 's' : ''} — review and edit before saving
              </div>
              {items.map((item, idx) => (
                <div key={idx} className={`border rounded p-3 ${item._saved ? 'border-green-300 bg-green-50' : item._saveError ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center min-w-0">
                      <FontAwesomeIcon icon={faFilePdf} className="text-indigo-500 mr-2 flex-shrink-0" />
                      <span className="text-[10px] text-gray-500 truncate">{item._filename}</span>
                      {!item._parseSuccess && (
                        <span className="ml-2 px-1.5 py-0.5 bg-yellow-100 text-yellow-700 text-[10px] rounded flex-shrink-0">Parse failed — fill manually</span>
                      )}
                    </div>
                    {item._saved && (
                      <span className="flex items-center text-[10px] text-green-700 font-medium flex-shrink-0 ml-2">
                        <FontAwesomeIcon icon={faCheckCircle} className="mr-1" /> Saved
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                    <div>
                      <label className="block text-gray-500 mb-0.5">Vendor / Account *</label>
                      <input type="text" value={item.accountName || item.vendor}
                        onChange={e => updateItem(idx, { accountName: e.target.value, vendor: e.target.value })}
                        disabled={item._saved}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-green-500 disabled:bg-gray-100" />
                    </div>
                    <div>
                      <label className="block text-gray-500 mb-0.5">Provider</label>
                      <select value={item.provider} onChange={e => updateItem(idx, { provider: e.target.value })}
                        disabled={item._saved}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-green-500 disabled:bg-gray-100">
                        <option value="aws">AWS</option>
                        <option value="gcp">GCP</option>
                        <option value="github">GitHub</option>
                        <option value="custom">Custom</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-gray-500 mb-0.5">Month *</label>
                      <input type="month" value={item.month}
                        onChange={e => updateItem(idx, { month: e.target.value })}
                        disabled={item._saved}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-green-500 disabled:bg-gray-100" />
                    </div>
                    <div>
                      <label className="block text-gray-500 mb-0.5">Amount *</label>
                      <div className="flex space-x-1">
                        <input type="number" step="0.01" min="0" value={item.amount}
                          onChange={e => updateItem(idx, { amount: Number(e.target.value) })}
                          disabled={item._saved}
                          className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-green-500 disabled:bg-gray-100" />
                        <select value={item.currency} onChange={e => updateItem(idx, { currency: e.target.value })}
                          disabled={item._saved}
                          className="px-1 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-green-500 disabled:bg-gray-100">
                          <option>USD</option><option>INR</option><option>EUR</option><option>GBP</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-gray-500 mb-0.5">Invoice #</label>
                      <input type="text" value={item.invoiceNumber}
                        onChange={e => updateItem(idx, { invoiceNumber: e.target.value })}
                        disabled={item._saved}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-green-500 disabled:bg-gray-100" />
                    </div>
                    <div>
                      <label className="block text-gray-500 mb-0.5">Due Date</label>
                      <input type="date" value={item.dueDate}
                        onChange={e => updateItem(idx, { dueDate: e.target.value })}
                        disabled={item._saved}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-green-500 disabled:bg-gray-100" />
                    </div>
                    {item.notes && (
                      <div className="col-span-2 sm:col-span-3">
                        <label className="block text-gray-500 mb-0.5">Notes (from AI)</label>
                        <div className="px-2 py-1 bg-gray-50 border border-gray-200 rounded text-[10px] text-gray-600">{item.notes}</div>
                      </div>
                    )}
                  </div>

                  {item._saveError && <div className="mt-2 text-[10px] text-red-600">{item._saveError}</div>}

                  {!item._saved && (
                    <div className="mt-2 flex justify-end">
                      <button onClick={() => saveItem(idx).then(b => { if (b) setSavedBills(prev => [...prev, b]); })}
                        disabled={item._saving}
                        className="px-3 py-1 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50">
                        {item._saving ? <><FontAwesomeIcon icon={faSpinner} className="animate-spin mr-1" />Saving...</> : 'Save'}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200">
          <div className="text-xs text-gray-500">
            {stage === 'review' && `${items.length - unsavedCount} / ${items.length} saved`}
          </div>
          <div className="flex items-center space-x-3">
            {stage === 'input' && (
              <>
                <button onClick={onClose} className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200">Cancel</button>
                {isPrivateError ? (
                  <button
                    onClick={() => googleLogin()}
                    className="flex items-center space-x-2 px-4 py-2 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
                  >
                    <FontAwesomeIcon icon={faGoogle} className="mr-1" />
                    Sign in with Google & Retry
                  </button>
                ) : (
                  <button onClick={() => handleFetch()} disabled={!isValidDriveUrl(folderUrl)}
                    className="px-4 py-2 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-40">
                    <FontAwesomeIcon icon={faFolderOpen} className="mr-2" />
                    Fetch & Parse
                  </button>
                )}
              </>
            )}
            {stage === 'review' && (
              <>
                {!allSaved && (
                  <button onClick={handleSaveAll} disabled={savingAll}
                    className="px-4 py-2 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50">
                    {savingAll ? <><FontAwesomeIcon icon={faSpinner} className="animate-spin mr-1" />Saving...</> : `Save All (${unsavedCount})`}
                  </button>
                )}
                <button onClick={handleDone} className="px-4 py-2 text-xs font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700">
                  {allSaved ? 'Done' : 'Skip & Close'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const BillsTab: React.FC<BillsTabProps> = ({ data, loading = false }) => {
  const [bills, setBills] = useState<Bill[]>([]);
  const [billsLoading, setBillsLoading] = useState(false);
  const [billsError, setBillsError] = useState<string | null>(null);

  const [selectedMonth, setSelectedMonth] = useState<string>(getCurrentMonth());
  const [filterProvider, setFilterProvider] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const [showBotModal, setShowBotModal]   = useState(false);
  const [showDriveModal, setShowDriveModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [form, setForm] = useState<BillFormState>(blankForm(getCurrentMonth()));
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedBill, setSelectedBill] = useState<Bill | null>(null);
  const [viewUrl, setViewUrl] = useState<string | null>(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState<string | null>(null);

  const { aiAnalysis, showAIInsights, analyzing, fetchAnalysis, setShowAIInsights } = useAIAnalysis('bills');

  const loadBills = useCallback(async () => {
    setBillsLoading(true);
    setBillsError(null);
    try {
      const result = await costService.getBills();
      setBills(result as Bill[]);
    } catch (err: any) {
      console.error('Failed to load bills:', err);
      setBillsError(err?.message || 'Failed to load bills');
    } finally {
      setBillsLoading(false);
    }
  }, []);

  useEffect(() => { loadBills(); }, [loadBills]);

  const navigateMonth = (direction: 'prev' | 'next') => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const date = new Date(year, month - 1, 1);
    date.setMonth(date.getMonth() + (direction === 'prev' ? -1 : 1));
    setSelectedMonth(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`);
  };

  const availableMonths = Array.from(new Set(bills.map(b => b.monthKey))).sort().reverse();

  const filteredBills = bills.filter(bill => {
    const matchesMonth = bill.monthKey === selectedMonth;
    const matchesProvider = filterProvider === 'all' || bill.provider === filterProvider;
    const matchesStatus = filterStatus === 'all' || bill.status === filterStatus;
    const q = searchTerm.toLowerCase();
    const matchesSearch = !q
      || bill.accountName.toLowerCase().includes(q)
      || bill.month.toLowerCase().includes(q)
      || bill.invoiceNumber?.toLowerCase().includes(q);
    return matchesMonth && matchesProvider && matchesStatus && matchesSearch;
  });

  const monthlyTotal = filteredBills.reduce((sum, bill) => sum + bill.amount, 0);
  const paidBills = filteredBills.filter(b => b.status === 'paid').length;
  const pendingBills = filteredBills.filter(b => b.status === 'pending').length;
  const totalAllBills = bills.reduce((sum, bill) => sum + bill.amount, 0);

  const handleAIAnalysis = async () => {
    await fetchAnalysis({
      bills: filteredBills,
      totalAmount: monthlyTotal,
      paidBills,
      pendingBills,
      summary: {
        totalAmount: monthlyTotal,
        totalCount: filteredBills.length,
        paidCount: paidBills,
        pendingCount: pendingBills,
        byProvider: bills.reduce((acc, bill) => {
          if (!acc[bill.provider]) acc[bill.provider] = { count: 0, amount: 0 };
          acc[bill.provider].count++;
          acc[bill.provider].amount += bill.amount;
          return acc;
        }, {} as Record<string, { count: number; amount: number }>)
      }
    });
  };

  // ----------------------------- Add Bill modal ------------------------------

  const openAddModal = () => {
    setForm(blankForm(selectedMonth));
    setSubmitError(null);
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setForm(blankForm(selectedMonth));
    setSubmitError(null);
  };

  const handleFilePick = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) setForm(prev => ({ ...prev, file }));
    if (event.target) event.target.value = '';
  };

  const handleSubmit = async () => {
    setSubmitError(null);

    // Client-side validation mirrors the backend's required-field check so the
    // user sees the failure inline instead of a 400 from the server.
    if (!form.monthKey) return setSubmitError('Month is required');
    if (!form.accountName.trim()) return setSubmitError('Account / vendor name is required');
    if (!form.amount || isNaN(Number(form.amount)) || Number(form.amount) < 0) {
      return setSubmitError('Amount must be a non-negative number');
    }

    setSubmitting(true);
    try {
      const created = await costService.createBill({
        month: form.monthKey,
        provider: form.provider,
        accountName: form.accountName.trim(),
        amount: Number(form.amount),
        currency: form.currency || 'USD',
        status: form.status,
        dueDate: form.dueDate || undefined,
        paidDate: form.paidDate || undefined,
        invoiceNumber: form.invoiceNumber.trim() || undefined,
        file: form.file
      });

      setBills(prev => [created as Bill, ...prev]);
      setSelectedMonth(form.monthKey);
      closeAddModal();
    } catch (err: any) {
      console.error('Create bill failed:', err);
      setSubmitError(err?.message || 'Failed to save bill');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (bill: Bill) => {
    if (!window.confirm(`Delete bill "${bill.invoiceNumber || bill.accountName}"? This also removes its uploaded PDF.`)) return;
    try {
      await costService.deleteBill(bill.id);
      setBills(prev => prev.filter(b => b.id !== bill.id));
      if (selectedBill?.id === bill.id) {
        setShowViewModal(false);
        setSelectedBill(null);
      }
    } catch (err: any) {
      alert(err?.message || 'Failed to delete bill');
    }
  };

  // ----------------------------- View / Download -----------------------------

  const handleViewInvoice = async (bill: Bill) => {
    setSelectedBill(bill);
    setShowViewModal(true);
    setViewUrl(null);
    setViewError(null);
    if (!bill.s3Key) return;
    setViewLoading(true);
    try {
      const url = await costService.getBillFileUrl(bill.id, true);
      setViewUrl(url);
    } catch (err: any) {
      console.error('View invoice failed:', err);
      setViewError(err?.message || 'Could not load invoice');
    } finally {
      setViewLoading(false);
    }
  };

  const handleDownloadInvoice = async (bill: Bill) => {
    if (!bill.s3Key) {
      alert('No PDF attached to this bill');
      return;
    }
    try {
      const url = await costService.getBillFileUrl(bill.id, false);
      // Open in new tab so the browser handles the Content-Disposition: attachment
      // and the user stays on the bills page.
      window.open(url, '_blank', 'noopener');
    } catch (err: any) {
      alert(err?.message || 'Failed to download invoice');
    }
  };

  // -------------------------------- Render ----------------------------------

  return (
    <div className="space-y-3 font-mono">
      {/* Header */}
      <div className="bg-white border border-gray-300 rounded p-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-bold text-gray-900 mb-1 flex items-center">
              <FontAwesomeIcon icon={faFileInvoice} className="text-indigo-600 mr-2 text-sm" />
              Bills & Invoices
            </h2>
            <p className="text-xs text-gray-600">
              Manual bill entries with optional PDF attachment. PDFs are stored privately in S3.
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <AIInsightsButton
              onClick={handleAIAnalysis}
              loading={analyzing}
              disabled={filteredBills.length === 0}
            />
            <button
              onClick={() => setShowBotModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-xs font-medium flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faRobot} />
              Auto Fetch
            </button>
            <button
              onClick={() => setShowDriveModal(true)}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors text-xs font-medium"
            >
              <FontAwesomeIcon icon={faFolderOpen} className="mr-2" />
              From Drive
            </button>
            <button
              onClick={() => setShowBulkModal(true)}
              className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors text-xs font-medium"
            >
              <FontAwesomeIcon icon={faUpload} className="mr-2" />
              Bulk Upload
            </button>
            <button
              onClick={openAddModal}
              className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors text-xs font-medium"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Add Bill
            </button>
          </div>
        </div>
      </div>

      {billsError && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded p-3">
          {billsError}
        </div>
      )}

      {showAIInsights && aiAnalysis && (
        <AIInsightsPanel analysis={aiAnalysis} onClose={() => setShowAIInsights(false)} />
      )}

      {/* Month Selector */}
      <div className="bg-white border border-gray-300 rounded p-3">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigateMonth('prev')}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
            title="Previous month"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>

          <div className="flex items-center space-x-3">
            <FontAwesomeIcon icon={faCalendarAlt} className="text-indigo-600" />
            <div className="text-center">
              <div className="text-sm font-bold text-gray-900">
                {formatMonthDisplay(selectedMonth)}
              </div>
              <div className="text-xs text-gray-600">
                ${monthlyTotal.toLocaleString()} • {filteredBills.length} bills
              </div>
            </div>
          </div>

          <button
            onClick={() => navigateMonth('next')}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
            title="Next month"
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>

        {availableMonths.length > 1 && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="text-xs text-gray-600 mb-2">Quick Jump:</div>
            <div className="flex flex-wrap gap-2">
              {availableMonths.map(month => (
                <button
                  key={month}
                  onClick={() => setSelectedMonth(month)}
                  className={`px-3 py-1 text-xs rounded transition-colors ${
                    month === selectedMonth
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {formatMonthDisplay(month)}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-300 rounded p-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-gray-600 mb-1">Month Total</div>
              <div className="text-lg font-bold text-gray-900">
                ${monthlyTotal.toLocaleString()}
              </div>
              <div className="text-[10px] text-gray-500">All time: ${totalAllBills.toLocaleString()}</div>
            </div>
            <div className="w-10 h-10 bg-blue-100 rounded flex items-center justify-center">
              <FontAwesomeIcon icon={faDollarSign} className="text-blue-600 text-xs" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-300 rounded p-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-gray-600 mb-1">Total Bills</div>
              <div className="text-lg font-bold text-gray-900">{filteredBills.length}</div>
            </div>
            <div className="w-10 h-10 bg-indigo-100 rounded flex items-center justify-center">
              <FontAwesomeIcon icon={faFileInvoice} className="text-indigo-600 text-xs" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-300 rounded p-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-gray-600 mb-1">Paid Bills</div>
              <div className="text-lg font-bold text-green-600">{paidBills}</div>
            </div>
            <div className="w-10 h-10 bg-green-100 rounded flex items-center justify-center">
              <FontAwesomeIcon icon={faCheck} className="text-green-600 text-xs" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-300 rounded p-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-gray-600 mb-1">Pending Bills</div>
              <div className="text-lg font-bold text-yellow-600">{pendingBills}</div>
            </div>
            <div className="w-10 h-10 bg-yellow-100 rounded flex items-center justify-center">
              <FontAwesomeIcon icon={faClock} className="text-yellow-600 text-xs" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-300 rounded p-3">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder="Search bills..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 pl-10 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
            <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-2.5 text-gray-400 text-xs" />
          </div>

          <select
            value={filterProvider}
            onChange={(e) => setFilterProvider(e.target.value)}
            className="px-3 py-2 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            <option value="all">All Providers</option>
            <option value="aws">AWS</option>
            <option value="gcp">GCP</option>
            <option value="github">GitHub</option>
            <option value="custom">Custom</option>
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="paid">Paid</option>
            <option value="pending">Pending</option>
            <option value="overdue">Overdue</option>
          </select>
        </div>
      </div>

      {/* Bills Table */}
      <div className="bg-white border border-gray-300 rounded overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-100 border-b border-gray-300">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Month</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Provider</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice#</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {billsLoading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-xs text-gray-600">
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                    Loading bills...
                  </td>
                </tr>
              ) : filteredBills.length > 0 ? (
                filteredBills.map((bill) => (
                  <tr key={bill.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <FontAwesomeIcon icon={faCalendar} className="text-gray-400 text-xs mr-2" />
                        <span className="text-xs font-medium text-gray-900">{bill.month}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <FontAwesomeIcon
                          icon={getProviderIcon(bill.provider)}
                          className={`${getProviderColor(bill.provider)} text-xs mr-2`}
                        />
                        <span className="text-xs text-gray-900 capitalize">{bill.provider.toUpperCase()}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-xs text-gray-900">{bill.accountName}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-xs text-gray-600">{bill.invoiceNumber || '—'}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-xs font-semibold text-gray-900">
                        ${bill.amount.toLocaleString()} {bill.currency}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-xs text-gray-600">
                        {bill.dueDate ? new Date(bill.dueDate).toLocaleDateString() : '—'}
                      </span>
                      {bill.paidDate && (
                        <div className="text-[10px] text-green-600">
                          Paid: {new Date(bill.paidDate).toLocaleDateString()}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">{getStatusBadge(bill.status)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs">
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => handleViewInvoice(bill)}
                          className={`${bill.s3Key ? 'text-indigo-600 hover:text-indigo-900' : 'text-gray-300 cursor-not-allowed'}`}
                          title={bill.s3Key ? 'View Invoice' : 'No PDF attached'}
                          disabled={!bill.s3Key}
                        >
                          <FontAwesomeIcon icon={faEye} className="text-xs" />
                        </button>
                        <button
                          onClick={() => handleDownloadInvoice(bill)}
                          className={`${bill.s3Key ? 'text-green-600 hover:text-green-900' : 'text-gray-300 cursor-not-allowed'}`}
                          title={bill.s3Key ? 'Download Invoice' : 'No PDF attached'}
                          disabled={!bill.s3Key}
                        >
                          <FontAwesomeIcon icon={faDownload} className="text-xs" />
                        </button>
                        <button
                          onClick={() => handleDelete(bill)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete bill"
                        >
                          <FontAwesomeIcon icon={faTrash} className="text-xs" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center">
                    <FontAwesomeIcon icon={faFileInvoice} className="text-4xl text-gray-300 mb-3" />
                    <div className="text-xs font-medium text-gray-900 mb-1">No Bills for {formatMonthDisplay(selectedMonth)}</div>
                    <div className="text-xs text-gray-600">
                      Click "Add Bill" to record a new invoice for this month.
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bot Automation Modal */}
      {showBotModal && (
        <BillBotModal
          onClose={() => setShowBotModal(false)}
          onSaved={() => loadBills()}
        />
      )}

      {/* Drive Import Modal */}
      {showDriveModal && (
        <DriveParseModal
          onClose={() => setShowDriveModal(false)}
          onSaved={(newBills) => {
            setBills(prev => [...newBills, ...prev]);
            if (newBills.length > 0) setSelectedMonth(newBills[0].monthKey || newBills[0].month?.substring(0, 7) || selectedMonth);
          }}
        />
      )}

      {/* Bulk Upload Modal */}
      {showBulkModal && (
        <BulkUploadModal
          onClose={() => setShowBulkModal(false)}
          onSaved={(newBills) => {
            setBills(prev => [...newBills, ...prev]);
            if (newBills.length > 0) setSelectedMonth(newBills[0].monthKey || newBills[0].month?.substring(0, 7) || selectedMonth);
          }}
        />
      )}

      {/* Add Bill Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900">Add Bill</h3>
                <button onClick={closeAddModal} className="text-gray-400 hover:text-gray-600">
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <div>
                  <label className="block text-gray-700 mb-1">Month *</label>
                  <input
                    type="month"
                    value={form.monthKey}
                    onChange={e => setForm(prev => ({ ...prev, monthKey: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 mb-1">Provider *</label>
                  <select
                    value={form.provider}
                    onChange={e => setForm(prev => ({ ...prev, provider: e.target.value as BillFormState['provider'] }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="custom">Custom (SaaS / vendor)</option>
                    <option value="aws">AWS</option>
                    <option value="gcp">GCP</option>
                    <option value="github">GitHub</option>
                  </select>
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-gray-700 mb-1">Account / Vendor name *</label>
                  <input
                    type="text"
                    placeholder={form.provider === 'aws' ? 'AWS account ID or alias' : 'e.g., Slack, DataDog'}
                    value={form.accountName}
                    onChange={e => setForm(prev => ({ ...prev, accountName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 mb-1">Invoice Number</label>
                  <input
                    type="text"
                    placeholder="INV-2026-04-001"
                    value={form.invoiceNumber}
                    onChange={e => setForm(prev => ({ ...prev, invoiceNumber: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 mb-1">Amount *</label>
                  <div className="flex space-x-2">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={form.amount}
                      onChange={e => setForm(prev => ({ ...prev, amount: e.target.value }))}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                    <select
                      value={form.currency}
                      onChange={e => setForm(prev => ({ ...prev, currency: e.target.value }))}
                      className="px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      <option value="USD">USD</option>
                      <option value="INR">INR</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 mb-1">Due Date</label>
                  <input
                    type="date"
                    value={form.dueDate}
                    onChange={e => setForm(prev => ({ ...prev, dueDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 mb-1">Paid Date</label>
                  <input
                    type="date"
                    value={form.paidDate}
                    onChange={e => setForm(prev => ({ ...prev, paidDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 mb-1">Status</label>
                  <select
                    value={form.status}
                    onChange={e => setForm(prev => ({ ...prev, status: e.target.value as BillFormState['status'] }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                    <option value="overdue">Overdue</option>
                  </select>
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-gray-700 mb-1">Invoice PDF (optional)</label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    onChange={handleFilePick}
                    className="hidden"
                  />
                  {form.file ? (
                    <div className="flex items-center justify-between border border-gray-300 rounded px-3 py-2">
                      <div className="flex items-center">
                        <FontAwesomeIcon icon={faFilePdf} className="text-indigo-600 mr-2" />
                        <div>
                          <div className="font-medium text-gray-900">{form.file.name}</div>
                          <div className="text-[10px] text-gray-500">{(form.file.size / 1024).toFixed(1)} KB</div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setForm(prev => ({ ...prev, file: null }))}
                        className="text-red-600 hover:text-red-800"
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full border-2 border-dashed border-gray-300 rounded px-3 py-4 text-gray-600 hover:border-indigo-500 hover:text-indigo-600 transition-colors"
                    >
                      <FontAwesomeIcon icon={faPaperclip} className="mr-2" />
                      Attach PDF, PNG or JPG (max 10MB)
                    </button>
                  )}
                </div>
              </div>

              {submitError && (
                <div className="mt-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded p-2">
                  {submitError}
                </div>
              )}

              <div className="flex justify-end space-x-3 mt-4">
                <button
                  onClick={closeAddModal}
                  disabled={submitting}
                  className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="px-4 py-2 text-xs font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700 disabled:opacity-50"
                >
                  {submitting ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                      Saving...
                    </>
                  ) : 'Save Bill'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Invoice Modal */}
      {showViewModal && selectedBill && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded shadow-xl max-w-5xl w-full mx-4 max-h-[95vh] overflow-y-auto">
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900">Invoice Details</h3>
                <button onClick={() => setShowViewModal(false)} className="text-gray-400 hover:text-gray-600">
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-gray-50 rounded p-3">
                  <div className="grid grid-cols-2 gap-4 text-xs">
                    <div>
                      <span className="text-gray-600">Provider:</span>
                      <div className="mt-1 flex items-center">
                        <FontAwesomeIcon
                          icon={getProviderIcon(selectedBill.provider)}
                          className={`${getProviderColor(selectedBill.provider)} mr-2`}
                        />
                        <span className="font-medium text-gray-900">{selectedBill.provider.toUpperCase()}</span>
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-600">Account:</span>
                      <div className="mt-1 font-medium text-gray-900">{selectedBill.accountName}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">Invoice Number:</span>
                      <div className="mt-1 font-medium text-gray-900">{selectedBill.invoiceNumber || '—'}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">Month:</span>
                      <div className="mt-1 font-medium text-gray-900">{selectedBill.month}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">Amount:</span>
                      <div className="mt-1 font-semibold text-lg text-gray-900">
                        ${selectedBill.amount.toLocaleString()} {selectedBill.currency}
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-600">Status:</span>
                      <div className="mt-1">{getStatusBadge(selectedBill.status)}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">Due Date:</span>
                      <div className="mt-1 font-medium text-gray-900">
                        {selectedBill.dueDate ? new Date(selectedBill.dueDate).toLocaleDateString() : '—'}
                      </div>
                    </div>
                    {selectedBill.paidDate && (
                      <div>
                        <span className="text-gray-600">Paid Date:</span>
                        <div className="mt-1 font-medium text-green-600">
                          {new Date(selectedBill.paidDate).toLocaleDateString()}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* PDF Preview */}
                {selectedBill.s3Key ? (
                  <div className="border border-gray-300 rounded overflow-hidden">
                    <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-300">
                      <div className="text-xs text-gray-700">
                        <FontAwesomeIcon icon={faFilePdf} className="text-indigo-600 mr-2" />
                        {selectedBill.fileName || 'invoice.pdf'}
                      </div>
                      <button
                        onClick={() => handleDownloadInvoice(selectedBill)}
                        className="text-xs text-green-700 hover:text-green-900"
                      >
                        <FontAwesomeIcon icon={faDownload} className="mr-1" />
                        Download
                      </button>
                    </div>
                    {viewLoading && (
                      <div className="p-8 text-center text-xs text-gray-600">
                        <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                        Loading invoice...
                      </div>
                    )}
                    {viewError && (
                      <div className="p-4 bg-red-50 border-t border-red-200 text-red-700 text-xs">
                        {viewError}
                      </div>
                    )}
                    {viewUrl && !viewLoading && (
                      <iframe
                        src={viewUrl}
                        title="Invoice PDF"
                        className="w-full"
                        style={{ height: '70vh', border: 0 }}
                      />
                    )}
                  </div>
                ) : (
                  <div className="border border-gray-300 rounded p-8 text-center">
                    <FontAwesomeIcon icon={faFilePdf} className="text-6xl text-gray-300 mb-4" />
                    <div className="text-xs text-gray-600">No PDF attached to this bill.</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
